import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  getTestSettings,
  getUserDataTheme,
  setTestSettings,
} from "../../store/login.slice";
import { useUpdateTestSettingsMutation } from "../../api/accountSlice/accountSlice";
import {
  addTestExtraSeconds,
  getTimerPaused,
  setTimerPaused,
} from "../../store/test.slice";
import {
  DEFAULT_TEST_SETTINGS,
  TestSettings,
} from "../../constants/testSettings";
import useSetUserTheme from "../../hooks/useSetUserTheme";
import styles from "./settingsBar.module.scss";

/*
 * Settings bar (pop-out panel).
 * Settings live in the store (state.user.userData.test_settings) and are
 * saved to the backend (debounced) whenever they change.
 * The appearance settings are applied by TestCenterColumns.
 * Confirm Answer Omission, Highlighter on and Show Timer are applied by the
 * test page. The rest of the feature toggles are saved but not used yet.
 * The theme picker saves the account theme (same as Account Settings) and
 * changes the whole app. "Add 5 more minutes" adds 300 seconds to the test
 * timer, resumes it and turns Show Timer on (not available when the page
 * isn't timed, e.g. in a completed test review).
 * "Pause timer" is not a saved setting: it uses timerPaused in the test store,
 * so it only lasts for the current test.
 */

const SAVE_DELAY_MS = 600;
const ADD_TIME_SECONDS = 300; // 5 minutes

// Same values Account Settings saves; the preview uses the theme-<key> class
const THEMES = [
  { key: "default", label: "Classic" },
  { key: "dark", label: "Dark" },
  { key: "ocean", label: "Lavender Mist" },
];

const FONTS = [
  "Times New Roman",
  "Courier New",
  "Verdana",
  "Georgia",
  "Arial",
  "Helvetica",
  "Garamond",
];

type ToggleKey =
  | "splitScreen"
  | "confirmAnswerOmission"
  | "highlighter"
  | "hidePercentages"
  | "strikethrough"
  | "showTimer"
  | "pauseTimer"
  | "showVotes";

// todo: saved like the others, but nothing on the test page uses it yet
const FEATURE_TOGGLES: { key: ToggleKey; label: string; }[] = [
  { key: "confirmAnswerOmission", label: "Confirm Answer Omission" },
  { key: "highlighter", label: "Highlighter on" },
  { key: "hidePercentages", label: "Hide % Choice Answered" }, // TODO: not implemented on the test page yet
  { key: "strikethrough", label: "Strikethrough on" },
  { key: "showTimer", label: "Show Timer" },
  { key: "pauseTimer", label: "Pause timer" },
  { key: "showVotes", label: "Show vote block" },
];

const ALIGN_OPTIONS = [
  { align: "start", Icon: AlignLeft, label: "Align left" },
  { align: "center", Icon: AlignCenter, label: "Align center" },
  { align: "end", Icon: AlignRight, label: "Align right" },
  { align: "full", Icon: AlignJustify, label: "Justify" },
];

const ZOOM_OPTIONS = [
  { size: 16, previewSize: "12px" },
  { size: 19, previewSize: "16px" },
  { size: 22, previewSize: "20px" },
];

function Toggle({
  isToggled,
  onToggle,
  label,
}: {
  isToggled: boolean;
  onToggle: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={isToggled}
      aria-label={label}
      className={`${styles.toggle} ${isToggled ? styles.toggleOn : ""}`}
      onClick={onToggle}
    />
  );
}

type SettingsBarProps = {
  isOpen: boolean;
  onClose: () => void;
  isTimed?: boolean; // false: "Add time" only shows a "not timed" toast
};

const SettingsBar = ({ isOpen, onClose, isTimed = true }: SettingsBarProps) => {
  const dispatch = useDispatch();
  const theme = useSelector(getUserDataTheme) || "default";
  const { applyTheme, isSavingTheme } = useSetUserTheme();
  const ui: TestSettings = useSelector(getTestSettings);
  const timerPaused: boolean = useSelector(getTimerPaused);

  //
  // Saving: the store updates immediately, the backend after a short pause
  // (so typing in the number inputs doesn't fire a request per keystroke).
  //
  const [saveTestSettings] = useUpdateTestSettingsMutation();
  const pendingSave = useRef<TestSettings | null>(null);
  const saveTimer = useRef<number | undefined>(undefined);

  const flushSave = useCallback(() => {
    window.clearTimeout(saveTimer.current);
    const settings = pendingSave.current;
    if (!settings) return;
    pendingSave.current = null;
    saveTestSettings({ settings })
      .unwrap()
      .catch((error) => {
        console.error("Failed to save test settings:", error);
        toast.error("Your settings couldn't be saved.");
      });
  }, [saveTestSettings]);

  const updateSettings = (patch: Partial<TestSettings>) => {
    const next = { ...ui, ...patch };
    dispatch(setTestSettings({ test_settings: next }));
    pendingSave.current = next;
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(flushSave, SAVE_DELAY_MS);
  };

  // Save straight away when the panel closes or the page unmounts
  useEffect(() => {
    if (!isOpen) flushSave();
  }, [isOpen, flushSave]);
  useEffect(() => () => flushSave(), [flushSave]);

  // Close with Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const toggle = (key: ToggleKey) => updateSettings({ [key]: !ui[key] });

  const isToggleOn = (key: ToggleKey) =>
    key === "pauseTimer" ? timerPaused && isTimed : ui[key];

  const onToggle = (key: ToggleKey) => {
    if (key !== "pauseTimer") {
      toggle(key);
      return;
    }
    if (!isTimed) {
      toast("This exam is not timed!");
      return;
    }
    dispatch(setTimerPaused({ paused: !timerPaused, at: Date.now() }));
  };

  const addTime = () => {
    if (!isTimed) {
      toast("This exam is not timed!");
      return;
    }
    // Resume first so the paused time is credited before the 5 minutes
    if (timerPaused) {
      dispatch(setTimerPaused({ paused: false, at: Date.now() }));
    }
    dispatch(addTestExtraSeconds({ testExtraSeconds: ADD_TIME_SECONDS }));
    if (!ui.showTimer) updateSettings({ showTimer: true });
    toast("Time added");
  };

  const selectTheme = async (key: string) => {
    if (key === theme || isSavingTheme) return;
    try {
      await applyTheme(key);
    } catch (error) {
      console.error("Failed to save theme:", error);
      toast.error("Your theme couldn't be saved.");
    }
  };

  return (
    <div className={isOpen ? styles.open : ""} aria-hidden={!isOpen}>
      <div className={styles.backdrop} onClick={onClose} />
      <aside
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
      >
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close settings"
        >
          <X size={20} />
        </button>

        <div className={styles.settingsBody}>
          <div className={styles.settingsTitle}>
            <h4>Settings</h4>
          </div>

          {/* Appearance */}
          <div className={styles.itemContainer}>
            <h5>Appearance</h5>
            <div className={styles.itemBody}>
              <div className={styles.themesContainer}>
                {THEMES.map(({ key, label }) => (
                  <div className={styles.themeContainer} key={key}>
                    <div className={`${styles.themeBox} theme-${key}`}>
                      <div
                        className={styles.cubeContainer}
                        onClick={() => selectTheme(key)}
                      >
                        <div className={styles.themeCube}></div>
                        <div className={styles.themeCube}></div>
                      </div>
                    </div>
                    <span>{label}</span>
                    <input
                      type="radio"
                      name="settings-theme"
                      aria-label={label}
                      checked={theme === key}
                      disabled={isSavingTheme}
                      onChange={() => selectTheme(key)}
                    />
                  </div>
                ))}
              </div>

              <div className={styles.itemRow}>
                <span>Split Screen Explanations</span>
                <Toggle
                  label="Split Screen Explanations"
                  isToggled={ui.splitScreen}
                  onToggle={() => toggle("splitScreen")}
                />
              </div>

              <div className={styles.itemRow}>
                <span>Choose Font</span>
                <select
                  className={styles.select}
                  value={ui.fontFamily}
                  style={{ fontFamily: ui.fontFamily }}
                  onChange={(e) =>
                    updateSettings({ fontFamily: e.target.value })
                  }
                >
                  {FONTS.map((font) => (
                    <option
                      key={font}
                      value={font}
                      style={{ fontFamily: font }}
                    >
                      {font}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.itemRow}>
                <span>Font Size</span>
                <div className={styles.counter}>
                  <button
                    type="button"
                    className={styles.fontButton}
                    onClick={() =>
                      updateSettings({ fontSize: Math.max(1, ui.fontSize - 1) })
                    }
                  >
                    -
                  </button>
                  <input
                    type="number"
                    className={styles.fontInput}
                    value={ui.fontSize}
                    onChange={(e) =>
                      updateSettings({
                        fontSize: Math.min(
                          30,
                          Math.max(1, Number(e.target.value)),
                        ),
                      })
                    }
                  />
                  <button
                    type="button"
                    className={styles.fontButton}
                    onClick={() =>
                      updateSettings({
                        fontSize: Math.min(30, ui.fontSize + 1),
                      })
                    }
                  >
                    +
                  </button>
                </div>
              </div>

              <div className={styles.itemRow}>
                <span>Text Zoom</span>
                <div className={styles.buttonGroup}>
                  {ZOOM_OPTIONS.map(({ size, previewSize }) => (
                    <button
                      key={size}
                      type="button"
                      aria-pressed={ui.textZoom === size}
                      className={`${styles.fontButton} ${
                        ui.textZoom === size ? styles.fontButtonSelected : ""
                      }`}
                      style={{ fontSize: previewSize }}
                      onClick={() => updateSettings({ textZoom: size })}
                    >
                      A
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.itemRow}>
                <span>Text Align</span>
                <div className={styles.buttonGroup}>
                  {ALIGN_OPTIONS.map(({ align, Icon, label }) => (
                    <button
                      key={align}
                      type="button"
                      aria-label={label}
                      aria-pressed={ui.textAlign === align}
                      className={`${styles.fontButton} ${
                        ui.textAlign === align ? styles.fontButtonSelected : ""
                      }`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onClick={() => updateSettings({ textAlign: align })}
                    >
                      <Icon size={14} />
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.itemRow}>
                <span>Content Width</span>
                <div className={styles.counter}>
                  <button
                    type="button"
                    className={styles.fontButton}
                    onClick={() =>
                      updateSettings({
                        contentWidth: Math.max(ui.contentWidth - 100, 0),
                      })
                    }
                  >
                    -
                  </button>
                  <input
                    type="number"
                    className={styles.fontInput}
                    style={{ width: 60 }}
                    value={ui.contentWidth}
                    onChange={(e) =>
                      updateSettings({
                        contentWidth: Math.max(0, Number(e.target.value)),
                      })
                    }
                  />
                  <button
                    type="button"
                    className={styles.fontButton}
                    onClick={() =>
                      updateSettings({ contentWidth: ui.contentWidth + 100 })
                    }
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className={styles.itemContainer}>
            <h5>Features</h5>
            <div className={styles.itemBody}>
              {FEATURE_TOGGLES.map(({ key, label }) => (
                <div className={styles.itemRow} key={key}>
                  <span>{label}</span>
                  <Toggle
                    label={label}
                    isToggled={isToggleOn(key)}
                    onToggle={() => onToggle(key)}
                  />
                </div>
              ))}

              <div className={styles.itemRow}>
                <span>Add time</span>
                <button
                  type="button"
                  className={styles.itemButton}
                  onClick={addTime}
                >
                  Add 5 more minutes
                </button>
              </div>

              <div className={styles.itemRow}>
                <span>Reset To Defaults</span>
                <button
                  type="button"
                  className={styles.itemButton}
                  onClick={() => updateSettings(DEFAULT_TEST_SETTINGS)}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default SettingsBar;